// Authentication utilities for admin access

export interface AdminUser {
  username: string;
  role: 'admin' | 'super_admin';
  displayName: string;
}

export interface AdminSession {
  authenticated: boolean;
  timestamp: number;
  expires: number;
  user?: AdminUser;
}

/**
 * Check if the current admin session is valid
 */
export const isAdminAuthenticated = (): boolean => {
  try {
    const sessionData = sessionStorage.getItem('adminSession');
    if (!sessionData) return false;

    const session: AdminSession = JSON.parse(sessionData);
    
    // Check if session has expired
    if (Date.now() > session.expires) {
      clearAdminSession();
      return false;
    }

    return session.authenticated;
  } catch (error) {
    // Handle error silently
    clearAdminSession();
    return false;
  }
};

/**
 * Get current authenticated user information
 */
export const getCurrentUser = (): AdminUser | null => {
  try {
    const sessionData = sessionStorage.getItem('adminSession');
    if (!sessionData) return null;

    const session: AdminSession = JSON.parse(sessionData);
    
    // Check if session has expired
    if (Date.now() > session.expires) {
      clearAdminSession();
      return null;
    }

    return session.user || null;
  } catch (error) {
    clearAdminSession();
    return null;
  }
};

/**
 * Clear the admin session
 */
export const clearAdminSession = (): void => {
  sessionStorage.removeItem('adminSession');
};

/**
 * Logout admin user
 */
export const logoutAdmin = (): void => {
  clearAdminSession();
  // Redirect to main page
  const url = new URL(window.location.href);
  url.searchParams.delete('admin');
  window.location.href = url.toString();
};

/**
 * Extend the current admin session
 */
export const extendAdminSession = (): void => {
  try {
    const sessionData = sessionStorage.getItem('adminSession');
    if (!sessionData) return;

    const session: AdminSession = JSON.parse(sessionData);
    if (session.authenticated && Date.now() < session.expires) {
      // Extend session by 2 hours from now
      session.expires = Date.now() + (2 * 60 * 60 * 1000);
      sessionStorage.setItem('adminSession', JSON.stringify(session));
    }
  } catch (error) {
    console.error('Error extending admin session:', error);
  }
};

/**
 * Get remaining session time in minutes
 */
export const getSessionTimeRemaining = (): number => {
  try {
    const sessionData = sessionStorage.getItem('adminSession');
    if (!sessionData) return 0;

    const session: AdminSession = JSON.parse(sessionData);
    const remaining = session.expires - Date.now();
    return Math.max(0, Math.floor(remaining / (60 * 1000)));
  } catch (error) {
    return 0;
  }
};

/**
 * Rate limiting for login attempts
 */
export const checkLoginRateLimit = (): { allowed: boolean; remainingTime?: number } => {
  const lastAttempt = localStorage.getItem('adminLastAttempt');
  const attemptCount = parseInt(localStorage.getItem('adminAttempts') || '0');
  const MAX_ATTEMPTS = 3;
  const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes
  
  if (lastAttempt && attemptCount >= MAX_ATTEMPTS) {
    const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt);
    if (timeSinceLastAttempt < LOCKOUT_TIME) {
      const remainingTime = Math.ceil((LOCKOUT_TIME - timeSinceLastAttempt) / 60000);
      return { allowed: false, remainingTime };
    } else {
      // Reset attempts after lockout period
      localStorage.removeItem('adminAttempts');
      localStorage.removeItem('adminLastAttempt');
    }
  }
  return { allowed: true };
};

/**
 * Record a failed login attempt
 */
export const recordFailedLoginAttempt = (): void => {
  const currentAttempts = parseInt(localStorage.getItem('adminAttempts') || '0') + 1;
  localStorage.setItem('adminAttempts', currentAttempts.toString());
  localStorage.setItem('adminLastAttempt', Date.now().toString());
};

/**
 * Clear failed login attempts (on successful login)
 */
export const clearFailedLoginAttempts = (): void => {
  localStorage.removeItem('adminAttempts');
  localStorage.removeItem('adminLastAttempt');
};